import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gridThemeNoBorder } from './gridTheme.js';
import AgGridReact from './AgGridReact.jsx';
import { DATA_TYPE_DEFINITIONS, getLocaleText } from './index.jsx';
import { useClientSideRows } from '../../datagrid/hooks/useClientSideRows.js';

function CommonAggridClientSideTable({
  fetchPath,
  fetchBody,
  quickFilterText,
  filterModel,
  columnDefs,
  gridOptions,
  containerStyle,
  onGridReady,
  gridRef: initialGridRef = null,
  baseKey = 'commonClientSideRows',
  resizeFunc = null
}) {
  const gridRef = initialGridRef || useRef(null);
  const containerRef = useRef(null);
  const { rowData, loading } = useClientSideRows({
    baseKey,
    fetchPath,
    fetchBody,
    quickFilterText,
    filterModel
  });

  const mergedGridOptions = useMemo(
    () => ({
      getLocaleText,
      rowModelType: 'clientSide',
      animateRows: true,
      ...DATA_TYPE_DEFINITIONS,
      ...gridOptions,
    }),
    [gridOptions]
  );

  const resizeGrid = (params) => {
    if (resizeFunc) {
      resizeFunc(params);
    } else {
      params.api?.sizeColumnsToFit();
    }
  };

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (!gridRef.current?.api) return;
      resizeGrid(gridRef.current);
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!gridRef.current?.api) return;
    if (loading) {
      gridRef.current.api.showLoadingOverlay();
    } else {
      gridRef.current.api.hideOverlay();
    }
  }, [loading]);

  return (
    <div
      className='grid-theme'
      ref={containerRef}
      style={{
        width: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        paddingBottom: '5rem',
        height: 'calc(100vh - 180px)',
        ...containerStyle,
      }}
    >
      <AgGridReact theme={gridThemeNoBorder}
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        gridOptions={mergedGridOptions}
        onGridReady={(params) => {
          onGridReady?.(params);
          if (loading) params.api.showLoadingOverlay();
          gridRef.current.api = params.api;
          resizeGrid(params);
        }}
        onFirstDataRendered={(params) => {
          gridOptions?.onFirstDataRendered?.(params);
          resizeGrid(params);
        }}
        onRowDataUpdated={() => {
          if (!gridRef.current?.api) return;
          loading
            ? gridRef.current.api.showLoadingOverlay()
            : gridRef.current.api.hideOverlay();
        }}
      />
    </div>
  );
}

export default CommonAggridClientSideTable;
